import { useState, type ReactNode } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { roleLabel } from '@/lib/roles';
import { trocarPapel, type EscritorioAdmin, type PapelAdmin } from '@/lib/plataforma_admin';
import { editarPessoa, type PessoaDetalhe, type PessoaEdicao } from '@/lib/plataforma_pessoa';
import { Button, Input, Select } from '@/components/ui';
import { PAPEIS_POR_AMBIENTE } from './UsuariosTable';

/** Executa a escrita e responde se ela aconteceu; `fechar` é para a exclusão. */
export type Executar = (
  acao: () => Promise<unknown>,
  mensagemSucesso: string,
  opcoes?: { fechar?: boolean },
) => Promise<boolean>;

export interface SecaoProps {
  pessoa: PessoaDetalhe;
  salvando: boolean;
  executar: Executar;
}

export function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="border-line flex flex-col gap-2 border-t pt-sc-2">
      <h3 className="text-heading-sm text-fg">{titulo}</h3>
      {children}
    </section>
  );
}

const _ROTULO = 'flex min-w-[180px] flex-1 flex-col gap-1 text-body-sm text-fg-muted';

/** Nome, e-mail e CPF; só o que mudou vai ao servidor. */
export function DadosSecao({ pessoa, salvando, executar }: SecaoProps) {
  const apiClient = useApiClient();
  const [nome, setNome] = useState(pessoa.display_name ?? '');
  const [email, setEmail] = useState(pessoa.email);
  const [cpf, setCpf] = useState(pessoa.cpf ?? '');

  function edicao(): PessoaEdicao {
    const pedido: PessoaEdicao = {};
    if (nome.trim() !== (pessoa.display_name ?? '')) pedido.display_name = nome.trim() || null;
    if (email.trim() !== pessoa.email) pedido.email = email.trim();
    if (cpf.replace(/\D/g, '') !== (pessoa.cpf ?? '')) pedido.cpf = cpf.trim() || null;
    return pedido;
  }

  return (
    <Secao titulo={ptBR.admin.pessoaSecaoDados}>
      <div className="flex flex-wrap gap-2">
        <label className={_ROTULO}>
          <span>{ptBR.admin.pessoaNome}</span>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="pessoa-nome" />
        </label>
        <label className={_ROTULO}>
          <span>{ptBR.admin.pessoaEmail}</span>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="pessoa-email"
          />
        </label>
        <label className={_ROTULO}>
          <span>{ptBR.admin.pessoaCpf}</span>
          <Input value={cpf} onChange={(e) => setCpf(e.target.value)} data-testid="pessoa-cpf" />
        </label>
      </div>
      <p className="text-caption text-fg-dim">{ptBR.admin.pessoaCpfHint}</p>
      <div>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={salvando || Object.keys(edicao()).length === 0}
          onClick={() =>
            void executar(
              () => editarPessoa(apiClient, pessoa.id, edicao()),
              ptBR.admin.acaoSucesso,
            )
          }
          data-testid="pessoa-salvar-dados"
        >
          {ptBR.admin.pessoaSalvarDados}
        </Button>
      </div>
    </Secao>
  );
}

/** O papel, com o que cada um faz; só os papéis que cabem no ambiente da pessoa. */
export function PapelSecao({ pessoa, salvando, executar }: SecaoProps) {
  const apiClient = useApiClient();
  const [papel, setPapel] = useState<PapelAdmin>(pessoa.role);

  return (
    <Secao titulo={ptBR.admin.pessoaSecaoPapel}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`papel-${pessoa.id}`}>
          {ptBR.admin.trocarPapelLabel}
        </label>
        <Select
          id={`papel-${pessoa.id}`}
          value={papel}
          disabled={salvando}
          onChange={(e) => setPapel(e.target.value as PapelAdmin)}
          data-testid={`papel-select-${pessoa.id}`}
        >
          {PAPEIS_POR_AMBIENTE[pessoa.ambiente].map((opcao) => (
            <option key={opcao} value={opcao}>
              {roleLabel(opcao)}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={salvando || papel === pessoa.role}
          onClick={() =>
            void executar(() => trocarPapel(apiClient, pessoa.id, papel), ptBR.admin.acaoSucesso)
          }
          data-testid={`papel-salvar-${pessoa.id}`}
        >
          {ptBR.admin.trocarPapelSubmit}
        </Button>
      </div>
      <p className="text-body-sm text-fg-muted" data-testid="pessoa-papel-descricao">
        {ptBR.admin.papelDescricao[papel]}
      </p>
    </Secao>
  );
}

interface EscritorioSecaoProps extends SecaoProps {
  escritorios: EscritorioAdmin[];
}

/** Mudar de escritório só vale para quem ainda não usou o sistema (ADR-0127 §5). */
export function EscritorioSecao({ pessoa, escritorios, salvando, executar }: EscritorioSecaoProps) {
  const apiClient = useApiClient();
  const [destino, setDestino] = useState(pessoa.escritorio_id ?? '');

  return (
    <Secao titulo={ptBR.admin.pessoaSecaoEscritorio}>
      {pessoa.tem_rastro ? (
        <>
          <p className="text-body-md text-fg">
            {pessoa.escritorio_nome ?? ptBR.admin.valorAusente}
          </p>
          <p className="text-body-sm text-fg-muted" data-testid="pessoa-mover-bloqueado">
            {ptBR.admin.pessoaMoverBloqueado}
          </p>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={destino}
            disabled={salvando}
            onChange={(e) => setDestino(e.target.value)}
            data-testid="pessoa-escritorio"
          >
            {escritorios.map((escritorio) => (
              <option key={escritorio.id} value={escritorio.id}>
                {escritorio.nome}
              </option>
            ))}
          </Select>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={salvando || destino === '' || destino === pessoa.escritorio_id}
            onClick={() =>
              void executar(
                () => editarPessoa(apiClient, pessoa.id, { escritorio_id: destino }),
                ptBR.admin.acaoSucesso,
              )
            }
            data-testid="pessoa-mover"
          >
            {ptBR.admin.pessoaMover}
          </Button>
        </div>
      )}
    </Secao>
  );
}
